import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/AlertDialog'
import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover'
import { useAnalytics } from 'hooks'
import { Clock, Loader2, Pause, Pencil, Play, Settings2, Trash2 } from 'lucide-react'
import { PlatformIcon } from 'components/PlatformIcon'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { AutopilotChannel, AutopilotDirection } from '../../types'
import { useAutopilotStore } from '../../store/autopilot-store'
import { useWeeklyQueueStore } from '../../store/weekly-queue-store'
import {
  AUTOPILOT_CHANNEL_OPTIONS,
  AUTOPILOT_DIRECTION_OPTIONS,
  AUTOPILOT_DIRECTION_VALUES,
  PRESET_DAYS,
  daysSummary,
  formatTimeLabel,
  sortWeekdays,
} from '../../utils/weekly-queue-utils'

export interface DefaultsPopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Open the full plan editor (the setup wizard, pre-filled). */
  onEdit?: () => void
}

/**
 * Anchored popover summarising the active autopilot plan. This is a *read-only*
 * snapshot — Schedule, what to post about, channels, publishing mode — followed
 * by three actions: Edit (opens the wizard), Pause/Resume, and Cancel. All
 * editing happens in the wizard now; the popover never mutates plan fields
 * inline.
 *
 * Pattern: parent owns `open`/`onOpenChange` and renders the trigger via
 * `PopoverTrigger` so external surfaces (cockpit header) can place the
 * anchor wherever they want.
 */
export function DefaultsPopover({
  open,
  onOpenChange,
  onEdit,
  children,
}: DefaultsPopoverProps & { children: React.ReactNode }) {
  const autopilots = useAutopilotStore(s => s.autopilots)
  const loaded = useAutopilotStore(s => s.loaded)
  const loading = useAutopilotStore(s => s.loading)
  const fetchAutopilots = useAutopilotStore(s => s.fetchAutopilots)
  const updateStatus = useAutopilotStore(s => s.updateStatus)
  const deleteAutopilot = useAutopilotStore(s => s.deleteAutopilot)
  const fetchQueue = useWeeklyQueueStore(s => s.fetchQueue)

  const plan = autopilots[0]
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const { track } = useAnalytics()

  const isPaused = plan?.status === 'paused'

  const handleToggleStatus = async () => {
    if (!plan || togglingStatus) return
    const from = plan.status
    const to = isPaused ? 'active' : 'paused'
    setTogglingStatus(true)
    try {
      await updateStatus(plan.id, to)
      track(AnalyticsEvents.Autopilot.AUTOPILOT_STATUS_CHANGED, {
        autopilot_id: plan.id,
        from_status: from,
        to_status: to,
      })
      void fetchQueue()
      setPauseConfirmOpen(false)
      toast.success(
        to === 'paused'
          ? 'Autopilot paused. Nothing will publish until you resume.'
          : 'Autopilot resumed.'
      )
    } catch (err) {
      console.error('Failed to toggle autopilot status:', err)
      toast.error('Couldn’t update your plan. Try again.')
    } finally {
      setTogglingStatus(false)
    }
  }

  const handleDelete = async () => {
    if (!plan || deleting) return
    setDeleting(true)
    try {
      await deleteAutopilot(plan.id)
      // "Autopilot Deleted" (with cancellation-cleanup counts) is emitted
      // server-side in AutopilotService.delete, after the cleanup runs.
      // Clear the weekly strip (it filters autopilotId IS NOT NULL) and close the
      // popover — the widget reverts to "Set up Autopilot" once the plan leaves
      // store state.
      void fetchQueue()
      setConfirmOpen(false)
      onOpenChange(false)
      toast.success('Autopilot plan cancelled.')
    } catch (err) {
      console.error('Failed to delete autopilot plan:', err)
      toast.error('Couldn’t cancel your plan. Try again.')
    } finally {
      setDeleting(false)
    }
  }

  const handleEdit = () => {
    onOpenChange(false)
    onEdit?.()
  }

  useEffect(() => {
    if (!open) return
    if (!loaded) void fetchAutopilots()
    track(AnalyticsEvents.Autopilot.AUTOPILOT_PLAN_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded, fetchAutopilots])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      track(AnalyticsEvents.Autopilot.AUTOPILOT_PLAN_CLOSED, { fields_changed: 0 })
    }
    onOpenChange(next)
  }

  const directions = plan ? readDirections(plan.contentSpecifics, plan.contentDirection) : []
  const channels = plan ? readChannels(plan.contentSpecifics, plan.channel) : []
  // A plan always has a cadence: prefer the explicit weekday set, otherwise
  // derive it from the coarse `frequency` preset (mirrors the wizard's prefill)
  // so the summary never reads "no days yet" for a real plan.
  const scheduleDays = !plan
    ? []
    : plan.preferredDays && plan.preferredDays.length > 0
      ? sortWeekdays(plan.preferredDays)
      : plan.frequency && plan.frequency !== 'custom'
        ? PRESET_DAYS[plan.frequency]
        : []
  const isAutomatic = plan?.publishMode === 'automatic'

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        variant="dark"
        align="end"
        sideOffset={8}
        className="w-[340px] p-0"
        onInteractOutside={e => {
          // The confirm dialog portals to <body>, so interacting with it reads as
          // an "outside" event for the popover. Keep the popover open while the
          // dialog is in play (the "Keep plan" path); we close it ourselves on a
          // successful delete.
          const target = e.target as HTMLElement | null
          if (
            target?.closest(
              '[data-slot="alert-dialog-overlay"], [data-slot="alert-dialog-content"]'
            )
          ) {
            e.preventDefault()
          }
        }}
      >
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Settings2 size={14} className="text-acid" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-paper">Autopilot plan</p>
            <p className="text-[11px] text-paper-dim">Your weekly posting plan, at a glance.</p>
          </div>
        </div>

        {!loaded || loading ? (
          <div className="flex h-32 items-center justify-center text-xs text-paper-dim">
            <Loader2 size={14} className="mr-2 animate-spin" />
            Loading…
          </div>
        ) : !plan ? (
          // Defensive only: the popover is always anchored on an existing plan
          // (no-plan surfaces route to the wizard instead), so this branch
          // should never render. Show a quiet message rather than an action.
          <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
            <p className="text-xs text-paper-dim">You don’t have a weekly plan yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3.5 p-4">
            <SummaryRow label="What">
              <ChipList>
                {directions.map(id => {
                  const opt = AUTOPILOT_DIRECTION_OPTIONS.find(o => o.id === id)
                  if (!opt) return null
                  return (
                    <Chip key={id}>
                      <span className="text-sm leading-none">{opt.emoji}</span>
                      {opt.label}
                    </Chip>
                  )
                })}
              </ChipList>
            </SummaryRow>

            <SummaryRow label="When">
              <div className="flex items-center gap-1.5 text-xs text-paper">
                <Clock size={13} className="text-paper-dim" />
                <span>
                  {scheduleDays.length > 0 ? daysSummary(scheduleDays) : 'Custom schedule'}
                  {plan.preferredTime ? ` · ${formatTimeLabel(plan.preferredTime)}` : ''}
                </span>
              </div>
            </SummaryRow>

            <SummaryRow label="Where">
              <ChipList>
                {channels.map(id => {
                  const opt = AUTOPILOT_CHANNEL_OPTIONS.find(o => o.id === id)
                  return (
                    <Chip key={id}>
                      <PlatformIcon channel={id} size={14} />
                      {opt?.label ?? id}
                    </Chip>
                  )
                })}
              </ChipList>
            </SummaryRow>

            <SummaryRow label="Approval">
              <div className="flex items-center gap-1.5 text-xs text-paper">
                <span className="text-sm leading-none">{isAutomatic ? '⚡' : '👀'}</span>
                <span>{isAutomatic ? 'Fully automatic' : 'Review first'}</span>
              </div>
              <p className="mt-1 text-[10px] text-paper-dim">
                {isAutomatic
                  ? 'Posts publish on schedule. No approval needed.'
                  : 'You approve each post before it publishes.'}
              </p>
            </SummaryRow>

            <div className="-mx-4 mt-1 -mb-4 flex flex-col gap-2 border-t border-line px-4 py-3">
              <Button
                type="button"
                variant="t3-tertiary"
                size="t3-sm"
                onClick={handleEdit}
                className="w-full"
                data-testid="plan-edit"
              >
                <Pencil size={14} />
                Edit plan
              </Button>
              <Button
                type="button"
                variant="t3-tertiary"
                size="t3-sm"
                onClick={() => {
                  // Resuming is safe — do it immediately. Pausing halts
                  // publishing, so confirm with an explainer first.
                  if (isPaused) void handleToggleStatus()
                  else setPauseConfirmOpen(true)
                }}
                disabled={togglingStatus}
                className="w-full"
                data-testid="plan-pause-toggle"
              >
                {togglingStatus ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : isPaused ? (
                  <Play size={14} />
                ) : (
                  <Pause size={14} />
                )}
                {isPaused ? 'Resume plan' : 'Pause plan'}
              </Button>
              <Button
                type="button"
                variant="t3-destructive"
                size="t3-sm"
                onClick={() => setConfirmOpen(true)}
                className="w-full"
                data-testid="plan-cancel"
              >
                <Trash2 size={14} />
                Cancel plan
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent variant="dark">
          <AlertDialogHeader>
            <AlertDialogTitle variant="dark">Cancel autopilot plan?</AlertDialogTitle>
            <AlertDialogDescription variant="dark">
              Taka stops drafting new posts. Anything still queued is unscheduled and kept as a
              draft you can publish or delete; posts still waiting for your approval are removed.
              This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                // Keep the dialog mounted while the delete is in flight; we close
                // it ourselves in handleDelete on success.
                e.preventDefault()
                void handleDelete()
              }}
              disabled={deleting}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : null}
              {deleting ? 'Cancelling…' : 'Cancel plan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pauseConfirmOpen} onOpenChange={setPauseConfirmOpen}>
        <AlertDialogContent variant="dark">
          <AlertDialogHeader>
            <AlertDialogTitle variant="dark">Pause autopilot?</AlertDialogTitle>
            <AlertDialogDescription variant="dark">
              Taka stops drafting and publishing new posts. Your plan settings and anything already
              scheduled stay put — resume anytime to pick up where you left off.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={togglingStatus}>Keep running</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                // Keep the dialog mounted while the request is in flight; we close
                // it ourselves in handleToggleStatus on success.
                e.preventDefault()
                void handleToggleStatus()
              }}
              disabled={togglingStatus}
              data-testid="plan-pause-confirm"
            >
              {togglingStatus ? <Loader2 size={14} className="animate-spin" /> : null}
              {togglingStatus ? 'Pausing…' : 'Pause plan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-1.5 text-[10px] font-semibold tracking-wide text-paper-dim uppercase">
        {label}
      </h4>
      {children}
    </section>
  )
}

function ChipList({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full border border-line bg-bg-2 px-2.5 py-1 text-xs text-paper">
      {children}
    </span>
  )
}

/**
 * Read the selected direction set out of the plan. Falls back to a
 * single-element array containing the legacy `contentDirection` anchor when
 * the plan was created before multi-select existed.
 */
function readDirections(
  contentSpecifics: Record<string, unknown> | undefined,
  legacy: AutopilotDirection | undefined
): AutopilotDirection[] {
  const raw = contentSpecifics?.directions
  if (Array.isArray(raw)) {
    const filtered = raw.filter((d): d is AutopilotDirection =>
      (AUTOPILOT_DIRECTION_VALUES as readonly string[]).includes(d as string)
    )
    if (filtered.length > 0) return filtered
  }
  return legacy ? [legacy] : []
}

/**
 * Read the selected channel set out of the plan. Falls back to a single-element
 * array containing the legacy `channel` anchor when the plan predates
 * multi-channel. Filters to channels we currently render so a legacy/hidden
 * channel (e.g. tiktok) doesn't leak a dead chip.
 */
function readChannels(
  contentSpecifics: Record<string, unknown> | undefined,
  legacy: AutopilotChannel | undefined
): AutopilotChannel[] {
  const valid = new Set<string>(AUTOPILOT_CHANNEL_OPTIONS.map(o => o.id))
  const raw = contentSpecifics?.channels
  if (Array.isArray(raw)) {
    const filtered = raw.filter((c): c is AutopilotChannel => valid.has(c as string))
    if (filtered.length > 0) return filtered
  }
  return legacy && valid.has(legacy) ? [legacy] : []
}
